import React, { useMemo } from "react";
import useVM from "@src/hooks/useVM";
import { makeAutoObservable } from "mobx";
import { RootStore, useStores } from "@stores";
import * as devicesService from "@services/devicesService";
import { toast } from "react-toastify";
import {
  TCreateDeviceParams,
  TDevice,
  TDeviceFarms,
  TDeviceActivity,
  TDeviceHealth,
  THealthDay,
} from "@services/devicesService";
import type { TActivityResponse } from "@services/devicesService";
import { getJohnDeereStatus } from "@services/johnDeereService";

/** Feature config gating "Import from John Deere" (Admin → Configs). */
export const JOHN_DEERE_COMBINES_CONFIG = "john_deere_combines";
const ctx = React.createContext<DevicesScreenVM | null>(null);

export const DevicesScreenVMProvider: React.FC = ({ children }) => {
  const rootStore = useStores();
  const store = useMemo(() => new DevicesScreenVM(rootStore), [rootStore]);
  return <ctx.Provider value={store}>{children}</ctx.Provider>;
};

export const useDevicesScreenVM = () => useVM(ctx);

class DevicesScreenVM {
  loading: boolean = false;
  private setLoading = (v: boolean) => (this.loading = v);

  initialized: boolean = false;
  private setInitialized = (v: boolean) => (this.initialized = v);

  search: string = "";
  setSearch = (v: string) => (this.search = v);

  // Farm filter pills (empty = show all). Devices visible under every farm
  // (allFarms) always match any active filter.
  farmFilters: string[] = [];
  toggleFarmFilter = (farmId: string) => {
    this.farmFilters = this.farmFilters.includes(farmId)
      ? this.farmFilters.filter((id) => id !== farmId)
      : [...this.farmFilters, farmId];
  };

  matchesFarmFilter = (device: TDevice): boolean => {
    if (this.farmFilters.length === 0) return true;
    const farms = device.farms ?? { allFarms: true, farmIds: [] };
    if (farms.allFarms) return true;
    return farms.farmIds.some((id) => this.farmFilters.includes(id));
  };

  editedDevice: TDevice | null = null;
  setEditedDevice = (v: TDevice | null) => (this.editedDevice = v);

  createDialogOpened: boolean = false;
  setCreateDialogOpened = (v: boolean) => (this.createDialogOpened = v);

  // "Import from John Deere" (feature config john_deere_combines): the button
  // shows only while the owner's John Deere connection is live.
  importJohnDeereOpened: boolean = false;
  setImportJohnDeereOpened = (v: boolean) => (this.importJohnDeereOpened = v);

  johnDeereConnected: boolean = false;
  private setJohnDeereConnected = (v: boolean) => (this.johnDeereConnected = v);

  /** Silent probe: a failure (or a team member, who cannot use JD) reads as not connected. */
  checkJohnDeere = () =>
    getJohnDeereStatus()
      .then((s) => this.setJohnDeereConnected(s.connected))
      .catch(() => this.setJohnDeereConnected(false));

  devices: TDevice[] = [];
  private setDevices = (v: TDevice[]) => (this.devices = v);

  // Per-day sensor health for the row sparklines, keyed by attachment id.
  health: Record<string, TDeviceHealth> = {};
  private setHealth = (v: Record<string, TDeviceHealth>) => (this.health = v);

  healthFor = (deviceId: string): THealthDay[] => this.health[deviceId]?.days ?? [];

  /** Channel the row's sparkline should plot; see `TDeviceHealth.channel`. */
  healthChannelFor = (deviceId: string): "value" | "freq" =>
    this.health[deviceId]?.channel ?? "value";

  // Sparse day -> working seconds for the harvest grid, keyed by attachment id.
  activity: Record<string, TDeviceActivity> = {};
  activityDays: number = 365;
  private setActivity = (v: TActivityResponse) => {
    this.activity = v.devices ?? {};
    this.activityDays = v.days ?? 365;
  };

  activityFor = (deviceId: string): TDeviceActivity => this.activity[deviceId] ?? {};

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
    this.sync().then(() => this.setInitialized(true));
    // Deliberately not awaited and not part of `initialized`: the sparkline is
    // decoration, and the device list must not wait on it to render.
    this.syncHealth();
    this.syncActivity();
  }

  syncActivity = () =>
    devicesService
      .getDevicesActivity()
      .then(this.setActivity)
      .catch(() => undefined);

  syncHealth = () =>
    devicesService
      .getDevicesHealth()
      .then(this.setHealth)
      // Already `silent` at the service layer — a missing chart shows as empty
      // sparklines, which is the correct degraded state, not an error.
      .catch(() => undefined);

  create = async (device: TDevice) => {
    this.setLoading(true);
    const params: TCreateDeviceParams = {
      serial_number: device.id,
      harvester: device.harvester,
    };
    devicesService
      .createDevice(params)
      .then(this.sync)
      .then(() => this.setCreateDialogOpened(false))
      .catch((e) => toast(e.message, { type: "error" }))
      .finally(() => this.setLoading(false));
  };

  /** `sendWidth: false`: the dialog's width field was left alone (TEditDeviceParams). */
  edit = async (device: TDevice, options?: { sendWidth?: boolean }) => {
    this.setLoading(true);
    devicesService
      .editDevice(device.id, {
        harvester: device.harvester,
        delay: device.delay,
        sendWidth: options?.sendWidth,
      })
      .then(() =>
        // Farm visibility rides along only when the dialog provided it
        // (owner editing an existing device).
        device.farms
          ? devicesService.setDeviceFarms(device.id, device.farms)
          : undefined
      )
      .then(this.sync)
      .then(() => this.setCreateDialogOpened(false))
      .catch((e) => toast(e.message, { type: "error" }))
      .finally(() => this.setLoading(false));
  };

  // Inline farm-tag editing from the device row (owner only).
  saveDeviceFarms = async (deviceId: string, farms: TDeviceFarms) => {
    try {
      await devicesService.setDeviceFarms(deviceId, farms);
      await this.sync();
    } catch (e: any) {
      toast(e.message, { type: "error" });
    }
  };

  sync = () =>
    devicesService
      .getDevicesData()
      .then(({ devices }) => this.setDevices(devices))
      .catch((e) => toast(e.message, { type: "error" }));

  get inactiveDevices() {
    return this.devices.filter(({ status }) => status === "inactive");
  }
  get activeDevices() {
    return this.devices.filter(({ status }) => status === "active");
  }
}
